/*@flow*/
import { useContext, useCallback } from 'react';
import * as I from 'immutable';
import isNil from 'lib/t/isNil';
import hasOwn from 'lib/hasOwn';
import isNumber from 'lib/t/isNumber';
import isString from 'lib/t/isString';
import removeNils from 'lib/removeNils';
import hash from 'lib/hash';
import commands from '../commands';
import FormContext from '../context';
import useField from './useField';
import useAttrs from './useAttrs';
import { getDefaultLabel } from '../util';
import { idTrans } from '../util/getTransformer';
import type { ValidationErrorT } from 'lib/t/types';
import type { TypeInfo, PathT, TransformerT } from '../types';

const eqArr = (arr1: any[], arr2: any[]): %checks =>
  arr1.length === arr2.length &&
  arr1.every((value, index) => value === arr2[index]);

function getLabel(
  key: ?(string | number),
  options: { label?: string | React$Node, legend?: string | React$Node }
): string | React$Node {
  const { label, legend } = options;
  if (hasOwn.call(options, 'label') || hasOwn.call(options, 'legend')) {
    return label || legend || null;
  }
  if (isNil(key) || isNumber(key)) return '';
  return getDefaultLabel(key);
}

function getError(
  errors: ValidationErrorT[],
  path: PathT,
  typeInfo: TypeInfo,
  value: any,
  options: $Shape<{
    error?: string | ((any, (string | number)[], any) => ?string),
  }>
): ?string {
  // If there is a hardcoded error in the options, return that.
  const error = options.error;
  if (isString(error)) return error;
  // Only select errors relevant to 'path'.
  const validationErrors = errors.filter((v) => eqArr(v.path, path));
  // Nothing to do.
  if (validationErrors.length === 0) return;
  // Now we know that error is a function, so we rename it for the sake of clarity.
  const mkError = error || typeInfo.getValidationErrorMessage;
  // Build error message using mkError function.
  if (mkError) return mkError(value, path, { validationErrors });
  // Return the first error message or null.
  return validationErrors.map((v) => v.message)[0];
}

export default function useLocals<T: {}>(
  path: PathT,
  options?: { transformer?: TransformerT }
): T {
  const { transformer = idTrans } = options || {};
  const ctx = useContext(FormContext);
  const { fieldOptions, typeInfo, fieldValue } = useField(path);
  const isRequired = !typeInfo.isMaybe;

  // Use the options at the path of this component to build the locals for
  // its template.
  const { help, config = {}, className, ...rest } = fieldOptions;
  const { errors } = ctx.state;
  const error = getError(errors, path, typeInfo, fieldValue, fieldOptions);
  const hasError = Boolean(error);

  const onChange = useCallback(
    (v) => {
      // Transform the value to its intended type
      // $FlowFixMe[prop-missing]
      const parsedValue = transformer.parse(v);

      commands(ctx).setValue(path, parsedValue);
    },
    [hash(ctx.state.value), transformer, path]
  );

  const lastKey = I.List(path).last();
  const label = getLabel(lastKey, fieldOptions);
  const format = transformer.format;
  // $FlowFixMe[prop-missing]
  const value = format ? format(fieldValue) : '';

  let attrs = useAttrs(path);

  return removeNils({
    // TODO: test
    ...rest,
    help,
    config,
    className,

    error,
    hasError,
    label,
    onChange,
    value,
    path,
    attrs,
    isRequired,
  });
}
